import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { canEditGlobalCatalog } from '../../../features/auth/admin-capabilities';
import { useAuth } from '../../../features/auth/auth-context';
import { listCitiesOptions } from '../../../features/cities/queries';
import { DeleteSourceChannelDialog } from '../../../features/source-channels/components/delete-source-channel-dialog';
import { SourceChannelForm } from '../../../features/source-channels/components/source-channel-form';
import {
  useDetachSourceChannelMessages,
  useFetchSourceChannelNow,
} from '../../../features/source-channels/mutations';
import { sourceChannelByIdOptions } from '../../../features/source-channels/queries';
import { PageHeader } from '../../../shared/components/page-header';
import { SuperadminCatalogNotice } from '../../../shared/components/superadmin-catalog-notice';

export const Route = createFileRoute('/_authenticated/source-channels/$channelId')({
  staticData: { breadcrumb: 'Канал' },
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(sourceChannelByIdOptions(params.channelId)),
      context.queryClient.ensureQueryData(listCitiesOptions()),
    ]);
  },
  component: EditSourceChannelPage,
});

const dateFmt = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function EditSourceChannelPage() {
  const router = useRouter();
  const { adminRoles } = useAuth();
  const canEdit = canEditGlobalCatalog(adminRoles);
  const { channelId } = Route.useParams();
  const { data: channel } = useSuspenseQuery(sourceChannelByIdOptions(channelId));
  const { data: cities } = useSuspenseQuery(listCitiesOptions());
  const fetchNow = useFetchSourceChannelNow(channelId);
  const detach = useDetachSourceChannelMessages(channelId);

  const handleFetchNow = async () => {
    try {
      await fetchNow.mutateAsync();
      toast.success('Запущена выборка');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось запустить выборку');
    }
  };

  const handleDetach = async () => {
    try {
      const result = await detach.mutateAsync();
      toast.success(`${result.detached} сообщений отвязано`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось отвязать сообщения');
    }
  };

  return (
    <div className="space-y-6">
      {!canEdit && <SuperadminCatalogNotice />}
      <PageHeader
        title={channel.title}
        description={
          <a
            href={`https://t.me/${channel.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs hover:underline"
          >
            @{channel.slug}
          </a>
        }
        actions={
          canEdit ? (
            <Button onClick={handleFetchNow} disabled={fetchNow.isPending} variant="outline">
              {fetchNow.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Получить сейчас
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Последняя выборка</div>
          <div>{channel.lastFetchedAt ? dateFmt.format(new Date(channel.lastFetchedAt)) : '—'}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Ошибки подряд</div>
          <div>
            {channel.consecutiveErrors > 0 ? (
              <Badge variant="destructive">{channel.consecutiveErrors}</Badge>
            ) : (
              <span>0</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Сообщений в базе</div>
          <div>{channel.messagesCount}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Статус</div>
          <div>
            {channel.isActive ? (
              <Badge variant="default">Активен</Badge>
            ) : (
              <Badge variant="secondary">Выключен</Badge>
            )}
          </div>
        </div>
      </div>

      {channel.lastError && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-md p-3 text-sm">
          <div className="font-semibold text-destructive mb-1">Последняя ошибка</div>
          <div className="font-mono text-xs break-all">{channel.lastError}</div>
        </div>
      )}

      <SourceChannelForm mode="edit" channel={channel} cities={cities} readOnly={!canEdit} />

      {canEdit && (
        <div className="border border-destructive/40 rounded-md p-4 space-y-3">
          <div>
            <h2 className="font-semibold text-destructive">Опасная зона</h2>
            <p className="text-xs text-muted-foreground">Необратимые действия — внимательно.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Отвязать сообщения</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Отвязать все сообщения канала?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Все собранные посты будут помечены как «не связаны с каналом» и не попадут в очередь
                    модерации повторно. Действие нельзя отменить.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDetach} disabled={detach.isPending}>
                    Отвязать
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <DeleteSourceChannelDialog
              channel={channel}
              onDeleted={() => router.navigate({ to: '/source-channels' })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
